import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { BASE_URL, IMAGE_BASE_URL, CDN_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const SubCategoryContext = createContext();
export const useSubCategories = () => useContext(SubCategoryContext);

export const SubCategoryProvider = ({ children }) => {
    const { token, adminId, isReady, logout } = useAuth();
    const [subCategories, setSubCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const mountedRef = useRef(true);

    const SUBCATEGORIES_ENDPOINT = "/subcategory";
    const baseURL = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;
    const imageURL = CDN_URL.endsWith("/") ? CDN_URL.slice(0, -1) : CDN_URL;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const safeSetSubCategories = (updater) => {
        if (!mountedRef.current) return;
        setSubCategories(updater);
    };

    const fetchSubCategories = useCallback(async () => {
        setLoading(true);
        try {
            if (!token) throw new Error("Missing auth token");

            const url = `${baseURL}${SUBCATEGORIES_ENDPOINT}`;
            const res = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
            });

            const payload = await res.json().catch(() => null);
            if (!res.ok) throw new Error(payload?.message || `Fetch failed (${res.status})`);

            const items = Array.isArray(payload?.data)
                ? payload.data
                : Array.isArray(payload?.subcategories)
                ? payload.subcategories
                : Array.isArray(payload)
                ? payload
                : payload?.items ?? [];

            const processed = items.map((c) => ({
                ...c,
                image: c.image ? `${imageURL}/${c.image}` : "/no-image.png",
                status: c.status ?? null,
            }));

            safeSetSubCategories(processed || []);
            return payload;
        } catch (err) {
            console.error("[SubCategories] fetchSubCategories error:", err);
            if (err.message?.includes("Token Signature could not be verified")) {
                logout();
                return;
            }
            safeSetSubCategories([]);
            throw err;
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [token, baseURL]);

    // --- Updated addSubCategory using FormData ---
    const addSubCategory = useCallback(
        async (formData) => {
            try {
            if (!token) throw new Error("Missing auth token");

            const url = `${baseURL}${SUBCATEGORIES_ENDPOINT}`;

            const res = await fetch(url, {
                method: "POST",
                headers: {
                Authorization: `Bearer ${token}`,
                Accept: "application/json",
                // DO NOT set Content-Type when sending FormData
                },
                body: formData,
            });

            const payload = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(payload?.message || `Add failed (${res.status})`);

            // Refresh list after adding
            await fetchSubCategories();
            return payload;
            } catch (err) {
            console.error("[SubCategories] addSubCategory error:", err);
            throw err;
            }
        },
        [token, baseURL, fetchSubCategories]
        );

    // --- Updated updateSubCategory using FormData ---
    const updateSubCategory = useCallback(
    async (id, formData) => {
        try {
        if (!token) throw new Error("Missing auth token");

        const url = `${baseURL}${SUBCATEGORIES_ENDPOINT}/${id}`;

        // Make sure we append update_audit_id
        if (!formData.get('update_audit_id')) {
            const auditId = localStorage.getItem("admin_id") || adminId || 1;
            formData.append('update_audit_id', auditId);
        }

        // Make sure category_id exists
        if (!formData.get('category_id')) {
            throw new Error("category_id is missing in FormData");
        }

        const res = await fetch(url, {
            method: "POST", // if backend expects PUT, change to PUT
            headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            },
            body: formData,
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || `Update failed (${res.status})`);

        await fetchSubCategories();
        return payload;
        } catch (err) {
        console.error("[SubCategories] updateSubCategory error:", err);
        throw err;
        }
    },
    [token, baseURL, fetchSubCategories, adminId]
    );

    const deleteSubCategory = useCallback(
        async (id) => {
            try {
                if (!token) throw new Error("Missing auth token");

                const auditId = localStorage.getItem("admin_id") || adminId || null;
                const url = `${baseURL}${SUBCATEGORIES_ENDPOINT}/${id}`;

                const res = await fetch(url, {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify({ delete_audit_id: auditId }),
                });

                const payload = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(payload?.message || `Delete failed (${res.status})`);

                safeSetSubCategories((prev) => prev.filter((c) => String(c.id) !== String(id)));

                return payload;
            } catch (err) {
                console.error("[SubCategories] deleteSubCategory error:", err);
                throw err;
            }
        },
        [token, baseURL, adminId]
    );

    const toggleStatus = useCallback(
        async (id, currentStatus) => {

            // Convert Active/Inactive properly
            const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

            try {
                if (!token) throw new Error("Missing auth token");

                const auditId = localStorage.getItem("admin_id") || adminId || null;

                // update UI instantly
                safeSetSubCategories((prev) =>
                    prev.map((c) =>
                        String(c.id) === String(id) ? { ...c, status: newStatus } : c
                    )
                );

                const url = `${baseURL}${SUBCATEGORIES_ENDPOINT}/${id}/status`;

                const res = await fetch(url, {
                    method: "PATCH",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify({
                        status: newStatus,
                        update_audit_id: auditId
                    }),
                });

                const payload = await res.json().catch(() => ({}));

                if (!res.ok) throw new Error(payload?.message || `Toggle failed (${res.status})`);

                return payload;

            } catch (err) {
                console.error("[SubCategories] toggleStatus error:", err);

                // rollback UI if failed
                safeSetSubCategories((prev) =>
                    prev.map((c) =>
                        String(c.id) === String(id) ? { ...c, status: currentStatus } : c
                    )
                );

                throw err;
            }
        },
        [token, baseURL, adminId]
    );



    useEffect(() => {
        if (!isReady) return;
        if (!token) return;
        fetchSubCategories().catch((err) =>
            console.warn("[SubCategories] initial fetch failed:", err.message)
        );
    }, [isReady, token]);

    return (
        <SubCategoryContext.Provider
            value={{
                subCategories,
                loading,
                fetchSubCategories,
                addSubCategory,
                updateSubCategory,
                deleteSubCategory,
                toggleStatus,
            }}
        >
            {children}
        </SubCategoryContext.Provider>
    );
};
